import { Container, Divider } from "@material-ui/core";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  CurrencyDisplay,
  CustomTable,
  DateRangePicker,
  ModalError,
  Page,
  SearchTextInput,
  SelectInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, formatPayloadDate, formatPeriodDate, getErrors } from "utils";

function UploadMonitoring() {
  const classes = globalStyles();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsType = urlParams.get("type");
  const [loadingPage, setLoadingPage] = useState(true);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    type: paramsType || "UD, IC, CU, CIU",
    start_date: null,
    end_date: null,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleChangeStartDate = date => {
    handleChangeQueryParams(date, "start_date");
  };

  const handleChangeEndDate = date => {
    handleChangeQueryParams(date, "end_date");
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const { start_date, end_date, ...otherParams } = queryParams;
      const res = await axios.get(`${hardBaseUrl}/process/datatable`, {
        headers,
        params: {
          ...otherParams,
          ...(start_date && { start_date: formatPayloadDate(start_date) }),
          ...(end_date && { end_date: formatPayloadDate(end_date) })
        },
      });

      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Upload Monitoring">
      <Container maxWidth={false}>
        <HeaderTitle
          title="Upload Monitoring"
          breadcrumbData={breadcrumbData}
        />
        <Divider className={classes.divider} />
        <Grid container justifyContent="space-between" mb={2}>
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e => handleChangeQueryParams(e?.target?.value, "search")}
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={2} alignItems="center" flexWrap="nowrap">
              <Grid item>
                <SelectInput
                  label="Upload Type"
                  value={queryParams?.type}
                  onChange={e => handleChangeQueryParams(e?.target?.value, "type")}
                  options={[
                    { value: "UD, IC, CU, CIU", name: "All" },
                    { value: "UD", name: "Upload DSP" },
                    { value: "IC", name: "Import Composer" },
                    { value: "CU", name: "Import Catalogue" },
                    { value: "CIU", name: "Upload Composer Invoice" },
                  ]}
                  optionKey="value"
                  optionLabel="name"
                  width={200}
                  disabled={loadingPage}
                />
              </Grid>
              <Grid item>
                <DateRangePicker
                  label="Upload Date"
                  startDate={queryParams?.start_date}
                  handleChangeStartDate={handleChangeStartDate}
                  endDate={queryParams?.end_date}
                  handleChangeEndDate={handleChangeEndDate}
                  width={250}
                  disabled={loadingPage}
                  useReset
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item =>
            history.push(`/admin/detail-upload-monitoring/${item?.id}`)
          }
          my={2}
        />
      </Container>
    </Page>
  );
}
const statusCondition = {
  Finish: "success",
  Error: "danger",
  Started: "warning",
  Initialize: "warning",
  Running: "warning",
};
const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Upload Monitoring",
    active: true,
  },
];
const columnTable = [
  {
    name: "id",
    title: "Upload ID",
  },
  {
    name: "type",
    title: "Upload Type",
  },
  {
    name: "header",
    title: "DSP",
    renderText: header => header?.properties?.sender_name || "-",
  },
  {
    name: "header",
    title: "Period",
    renderText: header => {
      const start = header?.start_date;
      const end = header?.end_date;
      return (start || end) ? formatPeriodDate(start, end) : "-";
    },
  },
  {
    name: "revenue",
    title: "Total Revenue",
    headerAlign: "right",
    renderText: revenue => (
      <CurrencyDisplay
        value={revenue}
        decimalScale={2}
        prefix="Rp"
      />
    ),
  },
  {
    name: "created_at",
    title: "Upload Date",
    renderText: date => formatDate(date),
  },
  {
    name: "status",
    title: "Upload Status",
    renderText: status => (
      <StatusChip label={status} type={statusCondition[status]} />
    ),
  },
];

export default UploadMonitoring;
